// Bulk photo upload for the host venue form: check, shrink, queue.
// Ledger: docs/ledgers/venue-editing/VENUE-EDITING-LEDGER.md, task V1.

export const MAX_PHOTOS = 50;
export const MAX_FILE_BYTES = 50_000_000;
export const MAX_EDGE = 2048;
export const JPEG_QUALITY = 0.8;
export const UPLOAD_CONCURRENCY = 3;
// Image bomb guard: a small file can claim a huge size and crash the tab when decoded.
// The largest normal cameras make about 60 megapixels.
export const MAX_PIXELS = 100_000_000;
// Most file names read from one folder or .zip file. Stops a freeze on a huge folder.
export const MAX_SCAN = 5000;

// Only these 4 formats reach the browser's image decoder (less attack surface).
// The type and name are a first filter (Windows gives HEIC an empty type, so the
// name counts too); sniffImage checks the real bytes.
const PHOTO_TYPE = /^image\/(jpe?g|pjpeg|png|webp|heic|heif)(-sequence)?$/i;
const PHOTO_NAME = /\.(jpe?g|jfif|png|webp|heic|heif)$/i;

// Path inside a folder or .zip file, or just the name.
export const pathOf = (file) => file.relativePath || file.webkitRelativePath || file.name || '';
const baseName = (path) => path.split('/').pop();

// Hidden and system files from Mac and Windows folders and zips. Skipped with no message.
export const isJunkPath = (path) => path.split('/').some((part) => part.startsWith('.') || part === '__MACOSX' || /^(thumbs\.db|desktop\.ini)$/i.test(part));

// A file name is shown in a message: remove control and text-direction characters
// (they can make "x\u202Egpj.exe" look like "xexe.jpg"), and keep it short.
export const cleanName = (name) => {
  const clean = String(name).replace(/[\u0000-\u001f\u007f-\u009f\u200b-\u200f\u202a-\u202e\u2066-\u2069\ufeff]/g, '');
  return clean.length > 40 ? `${clean.slice(0, 39)}…` : clean;
};

// Returns why a file cannot be added, or null.
export const checkFile = (file) => {
  if (!file.name || !(PHOTO_TYPE.test(file.type || '') || PHOTO_NAME.test(file.name))) {
    return 'not a photo';
  }
  if (Number(file.size) > MAX_FILE_BYTES) {
    return `over ${MAX_FILE_BYTES / 1_000_000} MB`;
  }
  return null;
};

// File-name order, numbers in natural order: 2.jpg before 10.jpg.
export const byFileName = (a, b) => pathOf(a).localeCompare(pathOf(b), undefined, { numeric: true, sensitivity: 'base' });

// Decide which picked files go in the queue. `currentCount` = photos saved + photos in progress.
export const planPick = (files, currentCount) => {
  const skipped = [];
  const valid = [];
  [...files]
    .filter((file) => !isJunkPath(pathOf(file)))
    .sort(byFileName)
    .forEach((file) => {
      const reason = checkFile(file);
      if (reason) skipped.push({ name: baseName(pathOf(file)) || 'file', reason });
      else valid.push(file);
    });
  const room = Math.max(0, MAX_PHOTOS - currentCount);
  return {
    accepted: valid.slice(0, room),
    skipped,
    overLimit: Math.max(0, valid.length - room),
    total: currentCount + Math.min(valid.length, room)
  };
};

const photos = (n) => `${n} photo${n === 1 ? '' : 's'}`;

// 3 or fewer: name each file. More: count them by reason.
const skippedMessage = (skipped) => {
  if (skipped.length <= 3) return `Not added: ${skipped.map((s) => `${cleanName(s.name)} (${s.reason})`).join(', ')}.`;
  const counts = {};
  skipped.forEach((s) => {
    counts[s.reason] = (counts[s.reason] || 0) + 1;
  });
  const reasons = Object.entries(counts).map(([reason, n]) => (reason === 'not a photo' ? (n === 1 ? '1 not a photo' : `${n} not photos`) : `${n} ${reason}`));
  return `Not added: ${skipped.length} files (${reasons.join(', ')}).`;
};

// The one message shown after a pick, or '' when all went in.
export const pickMessage = ({ accepted, skipped, overLimit, total }) => {
  const parts = [];
  if (overLimit > 0) {
    parts.push(
      accepted.length === 0
        ? `This venue has ${MAX_PHOTOS} photos, the most allowed. Delete a photo to add another.`
        : `Added ${photos(accepted.length)}. This venue now has ${total}, the most allowed. ${photos(overLimit)} ${overLimit === 1 ? 'was' : 'were'} not added.`
    );
  }
  if (skipped.length) parts.push(skippedMessage(skipped));
  return parts.join(' ');
};

// One line for every photo that could not be opened, so 3 failures do not say "This photo".
export const unreadableMessage = (n) =>
  n === 1 ? 'This photo could not be opened. Try a JPEG or PNG.' : `${n} photos could not be opened. Try JPEG or PNG files.`;

// Scale down to fit inside max × max. Never scale up.
export const fitWithin = (width, height, max = MAX_EDGE) => {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
};

// Tiles finish out of order. Photos join the saved list in pick order:
// take done tiles from the front, step over failed ones (they keep their tile
// and a Retry), and stop at the first tile still working.
export const takeCommittable = (tiles) => {
  const commit = [];
  for (const tile of tiles) {
    if (tile.status === 'done') commit.push(tile);
    else if (tile.status !== 'error') break;
  }
  return commit;
};

// Runs at most `n` tasks at the same time.
export const createLimiter = (n) => {
  let active = 0;
  const waiting = [];
  const next = () => {
    if (active >= n || !waiting.length) return;
    active += 1;
    const { task, resolve, reject } = waiting.shift();
    Promise.resolve()
      .then(task)
      .then(resolve, reject)
      .finally(() => {
        active -= 1;
        next();
      });
  };
  return (task) =>
    new Promise((resolve, reject) => {
      waiting.push({ task, resolve, reject });
      next();
    });
};

class UnreadablePhotoError extends Error {}
export const isUnreadable = (error) => error instanceof UnreadablePhotoError;

// Bytes start..end of a file. FileReader works in every target browser (and in tests).
export const readBytes = (blob, start, end) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob.slice(start, end));
  });

const u16be = (b, i) => (b[i] << 8) | b[i + 1];
const u32be = (b, i) => ((b[i] << 24) | (b[i + 1] << 16) | (b[i + 2] << 8) | b[i + 3]) >>> 0;
const u16le = (b, i) => b[i] | (b[i + 1] << 8);
const u24le = (b, i) => b[i] | (b[i + 1] << 8) | (b[i + 2] << 16);
const ascii = (b, i, n) => String.fromCharCode(...b.subarray(i, i + n));

// JPEG: walk the segments to the frame header (SOF), which holds the size.
const jpegSize = async (blob) => {
  let at = 2;
  for (let step = 0; step < 500 && at + 9 <= blob.size; step += 1) {
    const b = await readBytes(blob, at, at + 9);
    if (b[0] !== 0xff) return null;
    const marker = b[1];
    if (marker === 0xff) {
      at += 1; // fill byte
    } else if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd8)) {
      at += 2; // no length
    } else if (marker >= 0xc0 && marker <= 0xcf && marker !== 0xc4 && marker !== 0xc8 && marker !== 0xcc) {
      return { format: 'jpeg', width: u16be(b, 7), height: u16be(b, 5) };
    } else if (marker === 0xda || marker === 0xd9) {
      return null; // image data before any size
    } else {
      at += 2 + u16be(b, 2);
    }
  }
  return null;
};

// HEIC: the size is in "ispe" boxes inside the "meta" box. iPhone puts meta near
// the start; Samsung puts it at the end, after the image data. So walk the
// top-level boxes by their headers to find meta, then read only meta.
// A photo can have tiles; take the largest ispe.
const HEIC_BRANDS = /^(heic|heix|heim|heis|hevc|hevx|hevm|hevs|mif1|msf1)$/;
const MAX_META_BYTES = 4 * 1024 * 1024;
const heicSize = async (blob) => {
  let at = 0;
  for (let step = 0; step < 50 && at + 8 <= blob.size; step += 1) {
    const h = await readBytes(blob, at, at + 16);
    let size = u32be(h, 0);
    let header = 8;
    if (size === 1) {
      size = u32be(h, 8) * 2 ** 32 + u32be(h, 12); // 64-bit size
      header = 16;
    } else if (size === 0) {
      size = blob.size - at; // runs to the end of the file
    }
    if (size < header) return null;
    if (ascii(h, 4, 4) === 'meta') {
      if (size > MAX_META_BYTES) return null;
      const b = await readBytes(blob, at, at + size);
      let best = null;
      for (let i = 4; i + 16 <= b.length; i += 1) {
        if (b[i] === 0x69 && b[i + 1] === 0x73 && b[i + 2] === 0x70 && b[i + 3] === 0x65) {
          const width = u32be(b, i + 8);
          const height = u32be(b, i + 12);
          if (!best || width * height > best.width * best.height) best = { format: 'heic', width, height };
        }
      }
      return best;
    }
    at += size;
  }
  return null;
};

// The real format and size, from the file's own bytes ("magic bytes"), not its
// name or type. null = not a JPEG, PNG, WebP or HEIC we can read.
export const sniffImage = async (blob) => {
  const b = await readBytes(blob, 0, 32);
  if (b.length < 30) return null;
  if (b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff) return jpegSize(blob);
  if (u32be(b, 0) === 0x89504e47 && u32be(b, 4) === 0x0d0a1a0a && ascii(b, 12, 4) === 'IHDR') {
    return { format: 'png', width: u32be(b, 16), height: u32be(b, 20) };
  }
  if (ascii(b, 0, 4) === 'RIFF' && ascii(b, 8, 4) === 'WEBP') {
    const chunk = ascii(b, 12, 4);
    if (chunk === 'VP8 ') return { format: 'webp', width: u16le(b, 26) & 0x3fff, height: u16le(b, 28) & 0x3fff };
    if (chunk === 'VP8L') {
      const bits = (b[21] | (b[22] << 8) | (b[23] << 16) | (b[24] << 24)) >>> 0;
      return { format: 'webp', width: (bits & 0x3fff) + 1, height: ((bits >>> 14) & 0x3fff) + 1 };
    }
    if (chunk === 'VP8X') return { format: 'webp', width: u24le(b, 24) + 1, height: u24le(b, 27) + 1 };
    return null;
  }
  if (ascii(b, 4, 4) === 'ftyp' && HEIC_BRANDS.test(ascii(b, 8, 4))) return heicSize(blob);
  return null;
};

// Throws UnreadablePhotoError unless the bytes are an allowed format of a sane size.
export const checkImageBytes = async (blob) => {
  const info = await sniffImage(blob).catch(() => null);
  if (!info || !info.width || !info.height || info.width * info.height > MAX_PIXELS) {
    throw new UnreadablePhotoError(blob.name);
  }
  return info;
};

// A photo from a .zip file is unpacked only now, when its turn in the queue comes.
export const openSource = async (source) => {
  if (!source.open) return source;
  try {
    return await source.open();
  } catch (e) {
    throw new UnreadablePhotoError(source.name);
  }
};

const decodeWithImg = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => resolve({ source: img, width: img.naturalWidth, height: img.naturalHeight, done: () => URL.revokeObjectURL(url) });
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('img decode failed'));
    };
    img.src = url;
  });

const fromBitmap = (bitmap) => ({ source: bitmap, width: bitmap.width, height: bitmap.height, done: () => bitmap.close && bitmap.close() });

// The browser reads most files itself (Safari reads HEIC). heic-to (~3 MB) loads
// only when the browser cannot read a HEIC file.
const decode = async (file, format) => {
  try {
    return fromBitmap(await createImageBitmap(file, { imageOrientation: 'from-image' }));
  } catch (e) {
    // fall through
  }
  try {
    return await decodeWithImg(file);
  } catch (e) {
    // fall through
  }
  if (format === 'heic') {
    try {
      const { heicTo } = await import(/* webpackChunkName: "heic-to" */ 'heic-to');
      return fromBitmap(await heicTo({ blob: file, type: 'bitmap' }));
    } catch (e) {
      // fall through
    }
  }
  throw new UnreadablePhotoError(file.name);
};

const jpegName = (name) => `${name.replace(/\.[^.]+$/, '') || 'photo'}.jpg`;

// Any image → JPEG, at most MAX_EDGE px on the longest side. Re-drawing also
// removes EXIF data (GPS, camera), and only a real image can come out.
export const shrinkPhoto = async (file) => {
  const { format } = await checkImageBytes(file);
  const image = await decode(file, format);
  try {
    const { width, height } = fitWithin(image.width, image.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    // JPEG has no transparency: a transparent PNG would turn black.
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(image.source, 0, 0, width, height);
    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY));
    canvas.width = 0; // free the canvas memory now (Safari keeps it otherwise)
    if (!blob) throw new UnreadablePhotoError(file.name);
    return new File([blob], jpegName(file.name), { type: 'image/jpeg' });
  } finally {
    image.done();
  }
};
